import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
// Full namespace import. Do NOT switch to `crypto-js/core` + side-effect
// submodule imports (`import 'crypto-js/md5'` …): the production optimizer drops
// those bare imports, leaving CryptoJS.MD5/AES undefined at runtime (works in
// `ng serve`, throws "CryptoJS.MD5 is not a function" in built environments).
import * as CryptoJS from 'crypto-js';
import { environment } from '@environments/environment';
import { RawConfigResponse, RuntimeConfig } from '../models/runtime-config.model';
import { SentryService } from './sentry.service';

/** Envelope returned by POST /api/config. */
interface ConfigEnvelope {
  success: boolean;
  edata: string;
}

/**
 * Empty initial config. There is deliberately NO build-time fallback: the API
 * is the single source of truth (see runtime-config.model.ts). While unset, or
 * after a failed load, `s3BaseUrl` is '' and consumers degrade gracefully.
 */
const EMPTY_CONFIG: RuntimeConfig = { s3BaseUrl: '', serverPublicKey: '' };

const REQUEST_TIMEOUT_MS = 5000;

/**
 * ConfigService
 *
 * Fetches operational configuration from the Gozem config API instead of
 * hard-coding it in `environment.*.ts`. Triggered by `configResolver` on the
 * authenticated route area, so the request is always authenticated (the auth
 * interceptor attaches the Bearer token) and never runs for anonymous users.
 *
 * `load()` is idempotent: it fetches once on success and reuses the result; a
 * failed attempt is not cached, so a later navigation can retry.
 *
 * The response body (`edata`) is AES-256-ECB encrypted; the key is derived from
 * a salt embedded in the payload plus the MD5 of the request body (see
 * `decrypt()`). NOTE: client-side decryption is obfuscation, not
 * confidentiality — the key material ships in the bundle. Nothing truly secret
 * must transit this endpoint.
 */
@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly http = inject(HttpClient);
  private readonly sentry = inject(SentryService);

  private readonly _config = signal<RuntimeConfig>(EMPTY_CONFIG);
  readonly config = this._config.asReadonly();

  /** Ready-to-use base URL for S3 image/icon resolution. '' until loaded. */
  readonly s3BaseUrl = computed(() => this._config().s3BaseUrl);

  /** RSA public key (SPKI base64) used to encrypt the export PIN. '' until loaded. */
  readonly serverPublicKey = computed(() => this._config().serverPublicKey);

  /** Cached settled/in-flight load; reset on failure so a retry is possible. */
  private inFlight: Promise<void> | null = null;

  /**
   * Fetch and decrypt the runtime config. Idempotent and never rejects: on any
   * failure it reports to Sentry, keeps the empty config, and clears the cache
   * so a subsequent call can retry.
   */
  load(): Promise<void> {
    return (this.inFlight ??= this.fetchAndApply());
  }

  private async fetchAndApply(): Promise<void> {
    // The decryption key derives from md5(JSON.stringify(requestBody)); we send
    // an empty object and must hash the exact same value.
    const requestBody = {};
    try {
      const res = await firstValueFrom(
        this.http
          .post<ConfigEnvelope>(`${environment.apiUrl.trim()}/api/config`, requestBody)
          .pipe(timeout(REQUEST_TIMEOUT_MS)),
      );
      if (!res?.edata) throw new Error('Config API returned an empty payload');
      const raw = this.decrypt(res.edata, requestBody);
      if (!raw.image_base_url) throw new Error('Config payload is missing image_base_url');
      // server_public_key is intentionally NOT required here: a missing key must
      // not also blank out s3BaseUrl. PinEncryptionService reports + fails loudly
      // at encrypt time if the key is absent.
      this._config.set({
        s3BaseUrl: raw.image_base_url,
        serverPublicKey: raw.server_public_key ?? '',
      });
    } catch (err) {
      // No silent fallback: surface the failure and keep s3BaseUrl empty so
      // consumers degrade gracefully rather than pointing at a wrong bucket.
      this.sentry.captureException(err, { tags: { feature: 'runtime-config' } });
      this._config.set(EMPTY_CONFIG);
      this.inFlight = null; // allow a retry on the next trigger
    }
  }

  // ── Decryption (verified against a real /api/config sample) ────────────────

  private decrypt(
    edata: string,
    requestBody: unknown,
    algorithm = 'aes-256-ecb',
  ): RawConfigResponse {
    const hash = this.md5(JSON.stringify(requestBody));

    // Prefix: [0,3) = pk length (chars), [3,5) = rotation; then the pk block.
    const pkLen = Number(edata.substring(0, 3));
    const pkRot = Number(edata.substring(3, 5));
    const dataStart = 5 + pkLen;

    const pk = this.rotate(edata.substring(5, dataStart), pkRot);
    const pattern = CryptoJS.enc.Hex.parse(pk.substring(0, 32)).toString(CryptoJS.enc.Utf8);
    const timestamp = Number.parseInt(pk.substring(32), 16);

    const salt = this.fmtSaltUTC(timestamp, pattern);
    let keyStr = this.md5('GZ' + salt + hash);
    if (algorithm.includes('128')) keyStr = keyStr.substring(0, 16);
    else if (algorithm.includes('192')) keyStr = keyStr.substring(0, 24);

    const key = CryptoJS.enc.Utf8.parse(keyStr);
    const ciphertext = CryptoJS.enc.Hex.parse(edata.substring(dataStart));
    const decrypted = CryptoJS.AES.decrypt(CryptoJS.lib.CipherParams.create({ ciphertext }), key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8)) as RawConfigResponse;
  }

  private md5(value: string): string {
    return CryptoJS.MD5(value).toString();
  }

  private rotate(str: string, n: number): string {
    const l = str.length;
    n = ((n % l) + l) % l;
    return str.slice(n) + str.slice(0, n);
  }

  private padStart2(value: string | number): string {
    return String(value).padStart(2, '0');
  }

  private fmtSaltUTC(timestamp: number, pattern: string): string {
    const d = new Date(timestamp);
    const hours24 = d.getUTCHours();
    const tokens: Record<string, string> = {
      YYYY: '' + d.getUTCFullYear(),
      MM: this.padStart2(d.getUTCMonth() + 1),
      hh: this.padStart2(hours24 % 12 || 12),
      H: '' + hours24,
      m: '' + d.getUTCMinutes(),
      s: '' + d.getUTCSeconds(),
    };
    return pattern
      .split('-')
      .map((t) => tokens[t])
      .join('-');
  }
}
